import { sql, tx } from "@cashsync/powersync";
import type { SQL } from "drizzle-orm";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ `tx` CONTRIBUTION TO A BASE-CURRENCY TOTAL — single choke point         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Every expense/income total across the app (period summary, daily reports,
 * by account, by category, by project, day grouping in the list) is
 * expressed in the user's **base currency**. The rule is always the same:
 *
 *   · expense      → `base_amount`
 *   · income       → `base_amount`
 *   · transfer     → the principal DOES NOT count (moves money, neither creates nor
 *                    destroys it), but **the fee is real expense**.
 *
 * ─── The defect motivating this module ─────────────────────────────────────
 *
 * `fee_amount` is in the TRANSACTION currency (`tx.currency`), same as
 * `amount` — stamped that way by the form (`toMinorUnits(fee, currency)`) and
 * exported as such by CSV. But all six totals added it **raw** to an
 * accumulator of `base_amount`, which is already converted to the base currency:
 *
 *     WHEN kind = 'transfer' THEN COALESCE(fee_amount, 0)   -- ❌ unconverted
 *
 * With base currency = EUR and a transfer in JPY with a 500 ¥ fee, the
 * report added 500 **minor-euros** of expense: a ~×150 error in the single
 * number the user looks at. And it was invisible in mono-currency tests, because
 * there `base_amount == amount` and the fee was already in the right currency —
 * precisely why it survived the existing suite.
 *
 * The conversion is derived from the row itself (`base_amount / amount`) instead of
 * rereading the rate catalog: it is the SAME rate and the SAME rounding with which
 * the amount materialized (D2), so fee and principal cannot
 * disagree, and it works identically in SQL (reports) and TS (list
 * grouping) without depending on anything external.
 */

/**
 * Transfer fee expressed in BASE currency.
 *
 * - No fee ⇒ 0.
 * - Non-positive `amount` ⇒ no derivable ratio: fee is returned as-is
 *   (degenerate case; in mono-currency it is also the correct answer).
 * - Same base amount as nominal (mono-currency) ⇒ fee is already in base.
 */
export function transferFeeInBase(
  feeAmount: number | null | undefined,
  amount: number | null | undefined,
  baseAmount: number | null | undefined,
): number {
  const fee = feeAmount ?? 0;
  if (fee === 0) {
    return 0;
  }
  const nominal = amount ?? 0;
  const base = baseAmount ?? nominal;
  if (nominal <= 0 || base === nominal) {
    return fee;
  }
  return Math.round((fee * base) / nominal);
}

/**
 * SQL mirror of {@link transferFeeInBase}. Both are checked against the
 * SAME set of cases in `__tests__/money-sql.test.ts` (the test executes this
 * expression in real SQLite), so they cannot silently diverge.
 */
export function transferFeeInBaseSql() {
  return sql`CASE
    WHEN ${tx.fee_amount} IS NULL OR ${tx.fee_amount} = 0 THEN 0
    WHEN ${tx.amount} IS NULL OR ${tx.amount} <= 0 THEN ${tx.fee_amount}
    ELSE CAST(ROUND(${tx.fee_amount} * 1.0 * ${tx.base_amount} / ${tx.amount}) AS INTEGER)
  END`;
}

/**
 * Row contribution to period EXPENSE, in base currency. Use inside a
 * `SUM(...)`: this is the single place deciding what counts as expense.
 */
export function expenseContributionSql() {
  return sql`CASE
    WHEN ${tx.kind} = 'expense' THEN ${tx.base_amount}
    WHEN ${tx.kind} = 'transfer' THEN ${transferFeeInBaseSql()}
    ELSE 0 END`;
}

/** Row contribution to period INCOME, in base currency. */
export function incomeContributionSql() {
  return sql`CASE
    WHEN ${tx.kind} = 'income' THEN ${tx.base_amount}
    ELSE 0 END`;
}

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ `tx` CONTRIBUTION TO AN ACCOUNT BALANCE — single choke point            │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Balance is DERIVED (decision D4): `initial_balance` + the sum of this
 * contribution over live transactions. Nothing is converted here: amounts and
 * balance live in the account's own currency.
 *
 * ─── The defect motivating this choke point ────────────────────────────────
 *
 * The DESTINATION account branch of a transfer credited `tx.amount` —
 * the amount leaving the ORIGIN account, in origin currency — ignoring
 * `to_amount`, which is precisely the amount credited in the destination
 * currency and that the form already requested, saved, and displayed in history.
 *
 * In a transfer between accounts of the SAME currency the two values
 * coincide, so the suite (all mono-currency in transfers) passed;
 * in a 100 USD transfer to a JPY account, destination was credited with 10 000
 * minor units of USD read as if they were yen. Money was lost or
 * invented in balance, which is the opening number of the app.
 *
 * `COALESCE(to_amount, amount)` preserves exactly the previous behavior
 * when there is no `to_amount` (mono-currency and legacy rows).
 */
export function walletDeltaSql(walletRef: SQL, options: { clearedOnly?: boolean } = {}) {
  const clearedGuard = options.clearedOnly
    ? sql`WHEN COALESCE(${tx.cleared}, 0) = 0 THEN 0`
    : sql``;
  return sql`CASE
    WHEN ${tx.id} IS NULL THEN 0
    ${clearedGuard}
    WHEN ${tx.wallet_id} = ${walletRef} AND ${tx.kind} = 'income' THEN ${tx.amount}
    WHEN ${tx.wallet_id} = ${walletRef} AND ${tx.kind} = 'expense' THEN -${tx.amount}
    WHEN ${tx.wallet_id} = ${walletRef} AND ${tx.kind} = 'transfer' THEN -(${tx.amount} + COALESCE(${tx.fee_amount}, 0))
    WHEN ${tx.to_wallet_id} = ${walletRef} AND ${tx.kind} = 'transfer' THEN COALESCE(${tx.to_amount}, ${tx.amount})
    ELSE 0 END`;
}
